import React, { useState, useEffect, useMemo, useRef, useDeferredValue } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Search, Filter, Calendar, CheckCircle2, XCircle, Clock, 
  Image, Video, RefreshCw, User, MapPin, ChevronRight,
  AlertTriangle, TrendingUp, BarChart3, X, Globe, Users
} from 'lucide-react';
import { toast } from "sonner";
import { BACKEND_URL } from "../../config";
import { useRoleFlags } from '@/auth/useRoleFlags';
import { normalizeSearchText } from '@/utils/stringUtils';

const API = BACKEND_URL;

export default function TaskTrackingTab({ 
  userRole, 
  personnelId, 
  parks = [], 
  personnel = [],
  regions = [],
  onPersonnelClick 
}) {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total_count: 0, completed_count: 0, pending_count: 0, late_count: 0 });
  
  // Filtreler — tarih aralığı boş başlar; kullanıcı seçip "Ara"ya basana kadar fetch yapılmaz.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterPark, setFilterPark] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all'); // Bölge filtresi
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  
  // Park arama (autocomplete)
  const [parkSearchTerm, setParkSearchTerm] = useState('');
  const [showParkSuggestions, setShowParkSuggestions] = useState(false);
  const [selectedParkName, setSelectedParkName] = useState('');
  const parkInputRef = useRef(null);
  
  // Bölge arama (autocomplete)
  const [regionSearchTerm, setRegionSearchTerm] = useState('');
  const [showRegionSuggestions, setShowRegionSuggestions] = useState(false);
  const [selectedRegionName, setSelectedRegionName] = useState('');
  const regionInputRef = useRef(null);
  
  // Yönetici/Personel filtresi
  const [filterManager, setFilterManager] = useState('all');
  const [managerSearchTerm, setManagerSearchTerm] = useState('');
  const [showManagerSuggestions, setShowManagerSuggestions] = useState(false);
  const [selectedManagerName, setSelectedManagerName] = useState('');
  const managerInputRef = useRef(null);
  
  // Media Dialog
  const [mediaDialog, setMediaDialog] = useState({ open: false, urls: [], type: 'photo' });

  // Kullanıcının erişebileceği bölgeleri hesapla
  const accessibleRegions = useMemo(() => {
    const currentUser = personnel.find(p => p.id === personnelId);
    
    if (isMudur) {
      // Müdür tüm bölgeleri görebilir
      return regions;
    }
    
    if (!currentUser) return [];
    
    // Diğer roller için sadece kendine bağlı bölgeler
    const userRegionIds = currentUser.region_ids || [];
    return regions.filter(r => userRegionIds.includes(r.id));
  }, [userRole, personnelId, personnel, regions]);

  // Kullanıcının erişebileceği parkları hesapla
  const accessibleParks = useMemo(() => {
    const currentUser = personnel.find(p => p.id === personnelId);
    
    if (isMudur) {
      // Müdür tüm parkları görebilir
      return parks;
    }
    
    if (!currentUser) return [];
    
    // Kullanıcının bölgelerindeki parkları filtrele
    const userRegionIds = currentUser.region_ids || [];
    return parks.filter(park => {
      // Park'ın bölgesi kullanıcının bölgelerinde mi?
      return userRegionIds.includes(park.region_id);
    });
  }, [userRole, personnelId, personnel, parks]);

  // Park öneri listesi (yazılan metne göre filtreleme)
  const parkSuggestions = useMemo(() => {
    if (!parkSearchTerm.trim()) return [];
    const search = normalizeSearchText(parkSearchTerm);
    return accessibleParks
      .filter(park => normalizeSearchText(park.name).includes(search))
      .slice(0, 10); // Max 10 öneri
  }, [parkSearchTerm, accessibleParks]);

  // Bölge öneri listesi (yazılan metne göre filtreleme)
  const regionSuggestions = useMemo(() => {
    if (!regionSearchTerm.trim()) return [];
    const search = normalizeSearchText(regionSearchTerm);
    return accessibleRegions
      .filter(region => normalizeSearchText(region.name).includes(search))
      .slice(0, 10); // Max 10 öneri
  }, [regionSearchTerm, accessibleRegions]);

  // Yönetici listesi - Müdür Yardımcısı, Bölge Mühendisi, Formen
  const managerList = useMemo(() => {
    return personnel.filter(p => 
      ['mudur_yardimcisi', 'bolge_muhendisi', 'formen'].includes(p.role)
    ).sort((a, b) => {
      // Rol sırasına göre sırala
      const roleOrder = { mudur_yardimcisi: 1, bolge_muhendisi: 2, formen: 3 };
      return (roleOrder[a.role] || 99) - (roleOrder[b.role] || 99);
    });
  }, [personnel]);

  // Yönetici önerileri
  const managerSuggestions = useMemo(() => {
    if (!managerSearchTerm.trim()) return managerList.slice(0, 10);
    const search = normalizeSearchText(managerSearchTerm);
    return managerList
      .filter(p => normalizeSearchText(p.name).includes(search))
      .slice(0, 10);
  }, [managerSearchTerm, managerList]);

  // Seçilen yöneticiye bağlı personel ID'leri
  const getSubordinateIds = (managerId) => {
    if (!managerId || managerId === 'all') return null;
    
    const manager = personnel.find(p => p.id === managerId);
    if (!manager) return null;
    
    const subordinateIds = [managerId]; // Kendisi dahil
    
    if (manager.role === 'mudur_yardimcisi') {
      // Müdür Yardımcısına bağlı tüm personel
      const myRegionIds = manager.region_ids || [];
      personnel.forEach(p => {
        if (p.mudur_yardimcisi_id === managerId || 
            (p.region_ids && p.region_ids.some(r => myRegionIds.includes(r)))) {
          subordinateIds.push(p.id);
        }
      });
    } else if (manager.role === 'bolge_muhendisi') {
      // Bölge Mühendisine bağlı personel
      personnel.forEach(p => {
        if (p.bolge_muhendisi_id === managerId) {
          subordinateIds.push(p.id);
        }
      });
    } else if (manager.role === 'formen') {
      // Formene bağlı personel
      personnel.forEach(p => {
        if (p.formen_id === managerId) {
          subordinateIds.push(p.id);
        }
      });
    }
    
    return [...new Set(subordinateIds)];
  };

  // statusOverride: istatistik kartından gelen durum. setFilterStatus asenkron
  // olduğu için kartın seçtiği değeri doğrudan geçiyoruz; aksi hâlde bu fetch
  // bir önceki durumu kullanırdı. "Yenile"/"Ara" butonları bu fonksiyonu
  // doğrudan onClick'e verdiğinden ilk argüman event olabilir — o yüzden
  // yalnızca string kabul ediliyor.
  const fetchTasks = async (statusOverride) => {
    const effectiveStatus = typeof statusOverride === 'string' ? statusOverride : filterStatus;
    if (!startDate || !endDate) {
      toast.error('Lütfen tarih aralığı seçin (başlangıç ve bitiş).');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      if (userRole != null && userRole !== '') params.set('user_role', String(userRole));
      if (personnelId != null && personnelId !== '') params.set('user_personnel_id', String(personnelId));
      if (filterPark !== 'all') params.append('park_id', filterPark);
      if (filterRegion !== 'all') params.append('region_id', filterRegion);
      if (filterManager !== 'all') params.append('manager_id', filterManager);

      const response = await axios.get(`${API}/api/task-tracking?${params.toString()}`);
      let allTasks = response.data?.tasks ?? [];

      if (filterManager !== 'all') {
        const subordinateIds = getSubordinateIds(filterManager);
        if (subordinateIds) {
          allTasks = allTasks.filter(t => subordinateIds.includes(t.personnel_id));
        }
      }

      // Operasyondaki kişinin o günkü görevleri değerlendirmeye DAHİL EDİLMEZ
      setStats({
        total_count: allTasks.length,
        completed_count: allTasks.filter(t => t.completed && !t.in_operation).length,
        pending_count: allTasks.filter(t => !t.completed && !t.in_operation).length,
        late_count: allTasks.filter(t => t.is_late && !t.in_operation).length
      });

      let displayTasks = allTasks;
      if (effectiveStatus === 'completed') {
        displayTasks = allTasks.filter(t => t.completed && !t.in_operation);
      } else if (effectiveStatus === 'pending') {
        displayTasks = allTasks.filter(t => !t.completed && !t.in_operation);
      } else if (effectiveStatus === 'late') {
        displayTasks = allTasks.filter(t => t.is_late && !t.in_operation);
      }

      setTasks(displayTasks);
    } catch (error) {
      console.error('Görevler yüklenirken hata:', error);
      const isNetworkError = error.code === 'ERR_NETWORK' || error.message === 'Network Error' ||
        (error.message && String(error.message).toLowerCase().includes('network'));
      const isConnectionRefused = isNetworkError || (error.response == null && error.request != null);
      if (isConnectionRefused || isNetworkError) {
        toast.error(
          'Backend sunucusuna bağlanılamadı. API sunucusunun (backend) çalıştığından emin olun (örn. uvicorn ile port 8000 veya 8002).'
        );
      } else {
        const msg = error.response?.data?.detail;
        const errText = typeof msg === 'string'
          ? msg
          : Array.isArray(msg)
            ? msg.map(e => e?.msg ?? e).join(', ')
            : error.message || 'Görevler yüklenemedi';
        toast.error(errText || 'Görevler yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  // İstatistik kartından durum seçimi. Aynı karta tekrar tıklamak filtreyi
  // kaldırır ("Toplam Görev" zaten filtresiz demek). Otomatik fetch kuralını
  // bozmuyor: burada tetikleyen yine kullanıcının açık bir tıklaması.
  const applyStatusFilter = (key) => {
    const next = filterStatus === key ? 'all' : key;
    setFilterStatus(next);
    fetchTasks(next);
  };

  // Auto-fetch yok; tarih boş başlar, kullanıcı "Ara" butonu ile tetikler.
  // (Park/bölge/yönetici/durum filtreleri yalnızca arama yapıldıktan sonra
  // yeniden fetch için kullanılır; bunu da kullanıcı butonla yapsın.)

  // Click dışına tıklandığında önerileri kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (parkInputRef.current && !parkInputRef.current.contains(event.target)) {
        setShowParkSuggestions(false);
      }
      if (regionInputRef.current && !regionInputRef.current.contains(event.target)) {
        setShowRegionSuggestions(false);
      }
      if (managerInputRef.current && !managerInputRef.current.contains(event.target)) {
        setShowManagerSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Park seçimi
  const handleSelectPark = (park) => {
    setFilterPark(park.id);
    setSelectedParkName(park.name);
    setParkSearchTerm(park.name);
    setShowParkSuggestions(false);
  };

  // Park filtresini temizle
  const clearParkFilter = () => {
    setFilterPark('all');
    setSelectedParkName('');
    setParkSearchTerm('');
  };

  // Bölge seçimi
  const handleSelectRegion = (region) => {
    setFilterRegion(region.id);
    setSelectedRegionName(region.name);
    setRegionSearchTerm(region.name);
    setShowRegionSuggestions(false);
  };

  // Bölge filtresini temizle
  const clearRegionFilter = () => {
    setFilterRegion('all');
    setSelectedRegionName('');
    setRegionSearchTerm('');
  };

  // Yönetici seçimi
  const handleSelectManager = (manager) => {
    setFilterManager(manager.id);
    setSelectedManagerName(manager.name);
    setManagerSearchTerm(manager.name);
    setShowManagerSuggestions(false);
  };

  // Yönetici filtresini temizle
  const clearManagerFilter = () => {
    setFilterManager('all');
    setSelectedManagerName('');
    setManagerSearchTerm('');
  };

  // Rol badge'i
  const getRoleBadgeSmall = (role) => {
    const badges = {
      mudur_yardimcisi: { label: 'M.Yrd', color: 'bg-purple-100 text-purple-700' },
      bolge_muhendisi: { label: 'B.Müh', color: 'bg-blue-100 text-blue-700' },
      formen: { label: 'Formen', color: 'bg-orange-100 text-orange-700' }
    };
    const badge = badges[role] || { label: role, color: 'bg-gray-100 text-gray-700' };
    return <span className={`text-xs px-1.5 py-0.5 rounded ${badge.color}`}>{badge.label}</span>;
  };

  // Filtrelenmiş görevler
  const filteredTasks = useMemo(() => {
    if (!deferredSearchTerm) return tasks;
    const search = normalizeSearchText(deferredSearchTerm);
    return tasks.filter(task => (
      normalizeSearchText(task.personnel_name).includes(search) ||
      normalizeSearchText(task.original_name).includes(search) ||
      normalizeSearchText(task.substitute_name).includes(search) ||
      normalizeSearchText(task.park_name).includes(search) ||
      normalizeSearchText(task.task_name).includes(search)
    ));
  }, [tasks, deferredSearchTerm]);

  const getStatusBadge = (task) => {
    if (task.in_operation) {
      // O gün operasyonda → günlük görev durumu (eksik/gecikti) gösterilmez/sayılmaz
      return (
        <Badge className="bg-purple-100 text-purple-800 border border-purple-300"
          title={`Anlık görevlendirmede${task.operation_title ? ": " + task.operation_title : ""}${task.operation_location ? " · " + task.operation_location : ""} — günlük görevi değerlendirilmez`}>
          🟣 Toplu çalışmada{task.operation_location ? ` · ${task.operation_location}` : ""}
        </Badge>
      );
    }
    if (task.completed && task.is_late) {
      return <Badge className="bg-yellow-100 text-yellow-800">Geç Tamamlandı</Badge>;
    } else if (task.completed) {
      return <Badge className="bg-green-100 text-green-800">Tamamlandı</Badge>;
    } else if (task.is_late) {
      return <Badge className="bg-red-100 text-red-800">Gecikmiş</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800">Bekliyor</Badge>;
    }
  };

  const getTimeSlotLabel = (slot) => {
    const labels = {
      morning: '🌅 Sabah',
      noon: '☀️ Öğle',
      evening: '🌆 Akşam'
    };
    return labels[slot] || slot;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '-';
    }
  };

  const openMediaDialog = (task) => {
    const urls = task.media_urls?.length > 0 ? task.media_urls : (task.photo_url ? [task.photo_url] : []);
    if (urls.length > 0) {
      setMediaDialog({ open: true, urls, type: task.media_urls?.length > 0 ? 'mixed' : 'photo' });
    }
  };

  const handlePersonnelClick = (task) => {
    if (onPersonnelClick && task.personnel_id) {
      // Tarih aralığı ve park bilgisini de gönder
      onPersonnelClick({
        personnelId: task.personnel_id,
        startDate,
        endDate,
        parkId: task.park_id,
        parkName: task.park_name
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Günlük Görev Takip</h2>
          <p className="text-gray-500">Park görevlilerinin günlük görevlerini takip edin</p>
        </div>
        <Button onClick={fetchTasks} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* İstatistik Kartları — Durum filtresinin kısayolu. Kart durumu seçer ve
          listeyi hemen tazeler; aynı karta tekrar tıklamak filtreyi kaldırır. */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { key: 'all', label: 'Toplam Görev', value: stats.total_count, Icon: BarChart3, gradient: 'from-blue-500 to-blue-600', labelCls: 'text-blue-100' },
          { key: 'completed', label: 'Tamamlanan', value: stats.completed_count, Icon: CheckCircle2, gradient: 'from-green-500 to-green-600', labelCls: 'text-green-100' },
          { key: 'pending', label: 'Bekleyen', value: stats.pending_count, Icon: Clock, gradient: 'from-gray-500 to-gray-600', labelCls: 'text-gray-100' },
          { key: 'late', label: 'Gecikmiş', value: stats.late_count, Icon: AlertTriangle, gradient: 'from-red-500 to-red-600', labelCls: 'text-red-100' },
        ].map(({ key, label, value, Icon, gradient, labelCls }) => {
          const isActive = filterStatus === key;
          return (
            <Card
              key={key}
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              title={key === 'all' ? 'Durum filtresini kaldır' : `Yalnızca "${label}" görevleri göster`}
              onClick={() => applyStatusFilter(key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  applyStatusFilter(key);
                }
              }}
              className={`bg-gradient-to-br ${gradient} text-white cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 ${isActive ? 'ring-2 ring-offset-2 ring-gray-900 shadow-lg' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`${labelCls} text-sm`}>{label}</p>
                    <h3 className="text-2xl font-bold">{value}</h3>
                  </div>
                  <Icon className="h-8 w-8 opacity-80" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtreler */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtreler
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-4 items-end">
            {/* Tarih Aralığı */}
            <div className="md:col-span-2">
              <Label>Tarih Aralığı</Label>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={({ start, end }) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                placeholder="Tarih aralığı"
              />
            </div>
            
            {/* Park Filtresi - Autocomplete */}
            <div ref={parkInputRef} className="relative">
              <Label>Park</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <Input 
                  placeholder="Park adı yazın..." 
                  value={parkSearchTerm}
                  onChange={(e) => {
                    setParkSearchTerm(e.target.value);
                    setShowParkSuggestions(true);
                    if (!e.target.value) {
                      setFilterPark('all');
                      setSelectedParkName('');
                    }
                  }}
                  onFocus={() => setShowParkSuggestions(true)}
                  className="pl-10 pr-8"
                />
                {selectedParkName && (
                  <button
                    onClick={clearParkFilter}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Park önerileri */}
              {showParkSuggestions && parkSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {parkSuggestions.map(park => (
                    <button
                      key={park.id}
                      onClick={() => handleSelectPark(park)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm"
                    >
                      <MapPin className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="truncate">{park.name}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Sonuç yok mesajı */}
              {showParkSuggestions && parkSearchTerm && parkSuggestions.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-gray-500">
                  "{parkSearchTerm}" için park bulunamadı
                </div>
              )}
            </div>
            
            {/* Bölge Filtresi - Autocomplete */}
            <div ref={regionInputRef} className="relative">
              <Label>Bölge</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <Input 
                  placeholder="Bölge adı yazın..." 
                  value={regionSearchTerm}
                  onChange={(e) => {
                    setRegionSearchTerm(e.target.value);
                    setShowRegionSuggestions(true);
                    if (!e.target.value) {
                      setFilterRegion('all');
                      setSelectedRegionName('');
                    }
                  }}
                  onFocus={() => setShowRegionSuggestions(true)}
                  className="pl-10 pr-8"
                />
                {selectedRegionName && (
                  <button
                    onClick={clearRegionFilter}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Bölge önerileri */}
              {showRegionSuggestions && regionSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {regionSuggestions.map(region => (
                    <button
                      key={region.id}
                      onClick={() => handleSelectRegion(region)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm"
                    >
                      <Globe className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span className="truncate">{region.name}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Sonuç yok mesajı */}
              {showRegionSuggestions && regionSearchTerm && regionSuggestions.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-gray-500">
                  "{regionSearchTerm}" için bölge bulunamadı
                </div>
              )}
            </div>
            
            {/* Durum Filtresi */}
            <div>
              <Label>Durum</Label>
              <SearchableSelect
                value={filterStatus}
                onChange={setFilterStatus}
                allLabel="Tüm Durumlar"
                allValue="all"
                searchPlaceholder="Durum ara..."
                options={[
                  { id: "completed", name: "Tamamlanan" },
                  { id: "pending", name: "Bekleyen" },
                  { id: "late", name: "Gecikmiş" },
                ]}
              />
            </div>
            
            {/* Yönetici/Sorumlu Filtresi */}
            <div ref={managerInputRef} className="relative">
              <Label>Yönetici</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <Input 
                  placeholder="Yönetici ara..." 
                  value={managerSearchTerm}
                  onChange={(e) => {
                    setManagerSearchTerm(e.target.value);
                    setShowManagerSuggestions(true);
                    if (!e.target.value) {
                      setFilterManager('all');
                      setSelectedManagerName('');
                    }
                  }}
                  onFocus={() => setShowManagerSuggestions(true)}
                  className="pl-10 pr-8"
                />
                {selectedManagerName && (
                  <button
                    onClick={clearManagerFilter}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Yönetici önerileri */}
              {showManagerSuggestions && managerSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {managerSuggestions.map(manager => (
                    <button
                      key={manager.id}
                      onClick={() => handleSelectManager(manager)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-purple-600 flex-shrink-0" />
                        <span className="truncate">{manager.name}</span>
                      </div>
                      {getRoleBadgeSmall(manager.role)}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Sonuç yok mesajı */}
              {showManagerSuggestions && managerSearchTerm && managerSuggestions.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-gray-500">
                  "{managerSearchTerm}" için yönetici bulunamadı
                </div>
              )}
            </div>
            
            {/* Arama */}
            <div>
              <Label>Metin Ara</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Personel, park..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* ARA Butonu */}
            <div>
              <Label className="invisible">Ara</Label>
              <Button 
                onClick={fetchTasks} 
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Search className={`h-4 w-4 mr-2 ${loading ? 'animate-pulse' : ''}`} />
                {loading ? 'Aranıyor...' : 'Ara'}
              </Button>
            </div>
          </div>
          
          {/* Aktif filtreler göstergesi */}
          {(selectedParkName || selectedRegionName || selectedManagerName || filterStatus !== 'all') && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <span className="text-sm text-gray-500">Aktif Filtreler:</span>
              {selectedParkName && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {selectedParkName}
                  <button onClick={clearParkFilter} className="ml-1 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {selectedRegionName && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {selectedRegionName}
                  <button onClick={clearRegionFilter} className="ml-1 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {selectedManagerName && (
                <Badge variant="secondary" className="flex items-center gap-1 bg-purple-100 text-purple-700">
                  <Users className="h-3 w-3" />
                  {selectedManagerName}
                  <button onClick={clearManagerFilter} className="ml-1 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filterStatus !== 'all' && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  {filterStatus === 'completed' ? 'Tamamlanan' : filterStatus === 'pending' ? 'Bekleyen' : 'Gecikmiş'}
                  <button onClick={() => setFilterStatus('all')} className="ml-1 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Görev Tablosu */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Görev Listesi ({filteredTasks.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              {(!startDate || !endDate) ? (
                <>
                  <p className="font-medium">Tarih aralığı seçin</p>
                  <p className="text-sm mt-1">Başlangıç ve bitiş tarihini seçtikten sonra <strong>Ara</strong> butonuna tıklayın.</p>
                </>
              ) : (
                <>
                  <p className="font-medium">Bu kriterlere uygun görev bulunamadı</p>
                  <p className="text-sm mt-1">Tarih aralığını genişletin veya filtreleri değiştirip <strong>Ara</strong> butonuna tıklayın.</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Personel</TableHead>
                    <TableHead>Park</TableHead>
                    <TableHead>Görev</TableHead>
                    <TableHead>Zaman Dilimi</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Tamamlanma</TableHead>
                    <TableHead>Medya</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.map((task, idx) => (
                    <TableRow key={idx} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{task.date}</TableCell>
                      <TableCell>
                        {(() => {
                          const displayPersonnelName = (task.is_substitute && task.original_name)
                            ? task.original_name
                            : task.personnel_name;
                          const delegateName = (task.is_substitute && task.personnel_name)
                            ? task.personnel_name
                            : task.substitute_name;
                          return (
                        <button 
                          onClick={() => handlePersonnelClick(task)}
                          className="flex flex-col items-start gap-0.5 hover:text-blue-600 transition-colors text-left"
                        >
                          <span className="flex items-center gap-2">
                            <User className="h-4 w-4 shrink-0" />
                            {displayPersonnelName}
                          </span>
                          {(task.on_leave || task.is_substitute) && delegateName && (
                            <span className="text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                              Vekalet: {delegateName}
                            </span>
                          )}
                        </button>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-green-600" />
                          {task.park_name}
                        </div>
                      </TableCell>
                      <TableCell>{task.task_name}</TableCell>
                      <TableCell>{getTimeSlotLabel(task.time_slot)}</TableCell>
                      <TableCell>{getStatusBadge(task)}</TableCell>
                      <TableCell>{formatTime(task.completed_at)}</TableCell>
                      <TableCell>
                        {(task.photo_url || task.media_urls?.length > 0) ? (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openMediaDialog(task)}
                            className="text-blue-600"
                          >
                            <Image className="h-4 w-4 mr-1" />
                            {task.media_urls?.length || 1}
                          </Button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handlePersonnelClick(task)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Media Dialog */}
      <Dialog open={mediaDialog.open} onOpenChange={(open) => setMediaDialog({ ...mediaDialog, open })}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Görev Fotoğrafları/Videoları</DialogTitle>
            <DialogDescription>Park görevlisi tarafından yüklenen medya dosyaları</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
            {mediaDialog.urls.map((url, idx) => (
              <div key={idx} className="relative">
                {url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') ? (
                  <video 
                    src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`}
                    controls 
                    className="w-full rounded-lg"
                  />
                ) : (
                  <img 
                    src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`}
                    alt={`Medya ${idx + 1}`}
                    className="w-full rounded-lg object-cover"
                  />
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
